import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Alert, Button, Container, Field, Input, Panel } from '../components/site/ui';
import {
  IconCheck,
  IconCheckCircle,
  IconLock,
  IconMail,
  IconShield
} from '../components/icons';

const TRUST_POINTS = [
  {
    icon: IconShield,
    title: 'Confirms the case is genuine',
    description: 'Checks the case ID against our records — not a guess, a direct lookup.'
  },
  {
    icon: IconLock,
    title: 'No account needed',
    description: 'Anyone holding the case ID can verify it. Nothing is shared beyond this page.'
  },
  {
    icon: IconMail,
    title: 'Matches what you were sent',
    description: 'The merchant and order number shown should match the email or link you received.'
  }
];

const VerifyPage = () => {
  const [caseId, setCaseId] = useState('');
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setVerification(null);

    try {
      const response = await api.get(`/api/merchants/verify/${caseId}`);
      setVerification(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Case not found');
      } else {
        setError('Error verifying case');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="bg-brand-900">
        <Container width="full" className="py-10 sm:py-12">
          <p className="text-[11px] font-700 uppercase tracking-[0.14em] text-gold-400">
            Verify a case
          </p>
          <h1 className="mt-3 font-display text-[28px] font-800 leading-tight tracking-tight text-white sm:text-[36px]">
            Is this case <span className="text-accent-400">genuine?</span>
          </h1>
          <p className="mt-2.5 max-w-xl text-[15px] leading-relaxed text-white/75">
            If you&apos;ve received a ParcelBuddy link or email and want to confirm it&apos;s real,
            enter the case ID below.
          </p>
        </Container>
      </section>

      <Container width="full" className="py-10 sm:py-14">
        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] lg:gap-12">
          <div className="lg:pt-2">
            <ul className="space-y-3.5">
              {TRUST_POINTS.map(({ icon: Icon, title, description }) => (
                <li key={title} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100">
                    <Icon size={17} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-700 text-ink-900">{title}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-ink-600">{description}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-7 rounded-xl bg-ink-100 px-4 py-3.5">
              <p className="text-sm leading-relaxed text-ink-700">
                Not sure what&apos;s wrong with a delivery yet?{' '}
                <Link to="/report" className="font-600 text-brand-700 underline hover:text-brand-800">
                  Report an issue
                </Link>{' '}
                instead.
              </p>
            </div>
          </div>

          <Panel className="p-5 sm:p-6">
            <h2 className="font-display text-lg font-700 tracking-tight text-ink-900">
              Enter the case ID
            </h2>
            <p className="mt-1 text-sm text-ink-600">
              Shown in the email you received, e.g. PB-12345.
            </p>

            <form onSubmit={handleVerify} className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-end">
              <Field label="Case ID" htmlFor="caseId" required className="flex-1">
                <Input
                  type="text"
                  id="caseId"
                  value={caseId}
                  onChange={(e) => setCaseId(e.target.value.toUpperCase())}
                  placeholder="PB-12345"
                  required
                />
              </Field>

              <Button type="submit" size="lg" loading={loading} className="shrink-0 sm:w-auto">
                {loading ? 'Verifying…' : 'Verify case'}
              </Button>
            </form>

            {error && (
              <Alert tone="danger" className="mt-5" title={error}>
                Check the case ID and try again — it looks like PB-12345.
              </Alert>
            )}

            {verification && (
              <div className="mt-5 rounded-xl bg-success-50 p-5 ring-1 ring-inset ring-success-100">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success-100 text-success-700">
                    <IconCheckCircle size={19} />
                  </span>
                  <div className="min-w-0">
                    <p className="font-display text-[15px] font-700 leading-relaxed text-success-700">
                      Case {verification.case.caseId} is genuine
                    </p>
                    <p className="mt-0.5 text-sm text-ink-700">
                      Submitted on {new Date(verification.case.submittedAt).toLocaleDateString('en-GB')}
                    </p>

                    <dl className="mt-4 grid grid-cols-1 gap-3 border-t border-success-100 pt-4 sm:grid-cols-2">
                      <div>
                        <dt className="text-xs font-600 uppercase tracking-wide text-success-700/70">
                          Merchant
                        </dt>
                        <dd className="mt-0.5 text-sm font-600 text-ink-900">
                          {verification.case.merchantName}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-600 uppercase tracking-wide text-success-700/70">
                          Order
                        </dt>
                        <dd className="mt-0.5 text-sm font-600 text-ink-900">
                          {verification.case.orderNumber}
                        </dd>
                      </div>
                    </dl>

                    <p className="mt-4 flex items-start gap-1.5 text-xs leading-relaxed text-ink-500">
                      <IconCheck size={13} className="mt-0.5 shrink-0 text-success-600" />
                      This matches our records. If any other detail looks different, treat it with
                      caution.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Panel>
        </div>
      </Container>
    </>
  );
};

export default VerifyPage;
